import { z } from "zod";

import { fetchWithError, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

export const observationTypeListItemSchema = z.object({
  url: z.string(),
  id: z.number(),
  code: z.string(),
  parameter: z.string(),
  unit: z.string(),
  scale: z.string() /* ratio or interval */,
  description: z.string(),
  domain_values: z.null(),
  reference_frame: z.string().nullable(),
  compartment: z.string().nullable(),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
});

export type ObservationTypeListItem = z.infer<typeof observationTypeListItemSchema>;

export const observationTypeListSchema = paginatedSchema(observationTypeListItemSchema);
export type ObservationTypeList = z.infer<typeof observationTypeListSchema>;

/* There is no difference between list view and detail view
   for observation types */
export const observationTypeSchema = observationTypeListItemSchema;
export type ObservationType = ObservationTypeListItem;

export function useObservationTypes(params = {}) {
  const url = combineUrlAndParams("/api/v4/observationtypes/", params);
  return useParsedQuery<ObservationTypeList>(
    ["lizard-api-v4", "observationtypes", url],
    () => fetchWithError(url),
    (json: unknown) => observationTypeListSchema.parse(json)
  );
}

export function useObservationType(id: number) {
  return useParsedQuery<ObservationType>(
    ["lizard-api-v4", "observationtypes", id],
    () => fetchWithError(`/api/v4/observationtypes/${id}/`),
    (json: unknown) => observationTypeSchema.parse(json)
  );
}

export function getObservationTypeParameter(obs: ObservationType) {
  if (obs.unit && obs.unit !== "-") {
    return `${obs.parameter} (${getObservationTypeUnit(obs)})`;
  } else {
    return obs.parameter;
  }
}

export function getObservationTypeUnit(obs: ObservationType | null) {
  if (obs && obs.unit && obs.unit !== "-") {
    return `${obs.unit}${obs.reference_frame ? obs.reference_frame : ""}`;
  } else {
    return "";
  }
}
